import React from "react";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { Entity } from "@/model";
import { clickFromKeyboard } from "@/util/keyboardUtil";
import { getEntityType, getEntityTypeChipClassName } from "@/util/entityUtil";
import { useDialog, useModal } from "@/hook";
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from "@/component/Popover";
import HighlightText from "@/component/HighlightText";
import { DeleteIcon, EditIcon } from "@/assets/svgs/icons";

import { ENTITY_UPDATE_MODAL_ID } from "./EntityModifyModal";


interface EntityFragmentProps {
    entity: Entity;
    selectedIds: string[];
    searchQuery?: string;
    searchType?: string;
    onCheck: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onDelete: (id: string) => Promise<void>;
}

const EntityFragment: React.FC<EntityFragmentProps> = (props) => {
    const { entity, selectedIds, onCheck, searchQuery, searchType, onDelete } = props;

    const entityType = getEntityType(entity);

    const intl = useIntl();
    const navigate = useNavigate();
    const modal = useModal();
    const dialog = useDialog();

    const onClick = (id: string) => {
        if (searchType === "entity_value") {
            navigate(`${id}`, { state: { searchQuery } });
        } else {
            navigate(`${id}`);
        }
    };

    const onClickDelete = (id: string) => {
        dialog.open({
            title: intl.formatMessage({ id: "i05013" }),
            content: intl.formatMessage({ id: "i05023" }),
            variant: "danger",
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: async () => {
                await onDelete(id);
            },
        });
    };

    return (
        <div tabIndex={0}
             role="row"
             className="selection-table-tr group cursor-pointer"
             onClick={() => onClick(entity.id)}
             onKeyDown={clickFromKeyboard}
        >
            <div role="cell"
                 className="selection-table-td"
            >
                <input type="checkbox"
                       className={classNames(
                           selectedIds.length ? "opacity-100" : "opacity-0",
                           "selection-table-checkbox group-interaction",
                       )}
                       checked={selectedIds.includes(entity.id)}
                       onClick={e => e.stopPropagation()}
                       onChange={e => onCheck(e)}
                />
            </div>
            <div className="selection-table-td">
                {searchType === "name" ? (
                    <HighlightText query={entity.name}
                                   keyword={searchQuery}
                                   highlightClassName="text-highlight"
                    />
                ) : (
                    <span>{entity.name}</span>
                )}
            </div>
            <div role="cell"
                 className="selection-table-td pl-[15px]"
            >
                <div className={classNames(getEntityTypeChipClassName(entityType))}>
                     {entityType.toUpperCase()}
                </div>
            </div>
            <div role="cell"
                 className="selection-table-td"
            >
                <Popover>
                    {({ open, close }) => (
                        <>
                            <PopoverButton className="selection-table-popover-btn"
                                           open={open}
                                           close={close}
                            />
                            <PopoverMenu>
                                <PopoverItem onClick={() => modal.open(ENTITY_UPDATE_MODAL_ID, entity)}
                                            className="selection-table-popover-item"
                                >
                                    <EditIcon className="size-3.5"/>
                                    {intl.formatMessage({ id: "i05012" })}
                                </PopoverItem>
                                <PopoverItem onClick={() => onClickDelete(entity.id)}
                                            className="selection-table-popover-item text-point-red"
                                >
                                    <DeleteIcon className="size-3.5"/>
                                    {intl.formatMessage({ id: "i05013" })}
                                </PopoverItem>
                            </PopoverMenu>
                        </>
                    )}
                </Popover>
            </div>
        </div>
    );
};

export default EntityFragment;